import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TimePicker from "../time-picker/TimePicker";
import { isValidTimeFormat } from "../../utils/validation";

interface TimeSlot {
  value: string;
  label: string;
  disabled?: boolean;
}

interface TimeSelectorProps {
  selectedTime?: string;
  onTimeSelect?: (time: string) => void;
  className?: string;
  error?: string;
  disabled?: boolean;
  selectedDate?: Date;
}

const TimeSelector: React.FC<TimeSelectorProps> = ({
  selectedTime,
  onTimeSelect,
  className = "",
  error,
  disabled = false,
  selectedDate,
}) => {
  const { t } = useTranslation();

  // Helper function to determine which tab a time belongs to
  const getTabForTime = useCallback((time: string): number => {
    if (!time) return 0; // Default to morning tab if no time

    const [hours] = time.split(":").map(Number);

    if (hours >= 6 && hours < 12) {
      return 0; // Morning: 6:00-11:59
    } else if (hours >= 12 && hours < 18) {
      return 1; // Midday: 12:00-17:59
    } else {
      return 2; // Evening: 18:00-23:59 (and 0:00-5:59 for late night/early morning)
    }
  }, []);

  // Helper function to check if a time slot is in the past based on user's local time
  const isTimeInPast = useCallback(
    (timeValue: string): boolean => {
      if (!selectedDate) return false;

      // Get current date and time in user's local timezone
      const now = new Date();
      const [hours, minutes] = timeValue.split(":").map(Number);

      // Create a date object for the selected date with the time slot in user's local timezone
      // We use the selectedDate but set the time to the specific slot time
      const timeSlotDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        hours,
        minutes,
        0,
        0
      );

      // Compare the time slot with current user's local time
      // Both dates are in user's local timezone, so comparison is accurate
      return timeSlotDate < now;
    },
    [selectedDate]
  );

  // Helper function to find the best default tab based on current time and available slots
  const getBestDefaultTab = useCallback((): number => {
    if (!selectedDate) return 0; // Default to morning if no date selected

    const now = new Date();
    const currentHour = now.getHours();

    // If selected date is today, find the next available time period
    const isToday = selectedDate.toDateString() === now.toDateString();

    if (isToday) {
      // For today, show the tab that contains the next available time slots
      if (currentHour < 12) {
        return 0; // Morning
      } else if (currentHour < 18) {
        return 1; // Midday
      } else {
        return 2; // Evening
      }
    } else {
      // For future dates, default to morning
      return 0;
    }
  }, [selectedDate]);

  const [activeTab, setActiveTab] = useState(() => {
    // If there's a selected time, use its tab, otherwise use the best default
    return selectedTime ? getTabForTime(selectedTime) : getBestDefaultTab();
  });
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customTime, setCustomTime] = useState("");
  const [customTimeError, setCustomTimeError] = useState("");
  const [isManualTabChange, setIsManualTabChange] = useState(false);

  const timeSlots: {
    morning: { primary: TimeSlot[]; secondary: TimeSlot[] };
    midday: { primary: TimeSlot[]; secondary: TimeSlot[] };
    evening: { primary: TimeSlot[]; secondary: TimeSlot[] };
  } = {
    morning: {
      primary: [
        { value: "08:00", label: "8:00 AM", disabled: isTimeInPast("08:00") },
        { value: "08:30", label: "8:30 AM", disabled: isTimeInPast("08:30") },
        { value: "09:00", label: "9:00 AM", disabled: isTimeInPast("09:00") },
        { value: "09:30", label: "9:30 AM", disabled: isTimeInPast("09:30") },
        { value: "10:00", label: "10:00 AM", disabled: isTimeInPast("10:00") },
        { value: "10:30", label: "10:30 AM", disabled: isTimeInPast("10:30") },
        { value: "11:00", label: "11:00 AM", disabled: isTimeInPast("11:00") },
        { value: "11:30", label: "11:30 AM", disabled: isTimeInPast("11:30") },
      ],
      secondary: [
        { value: "06:00", label: "6:00 AM", disabled: isTimeInPast("06:00") },
        { value: "06:30", label: "6:30 AM", disabled: isTimeInPast("06:30") },
        { value: "07:00", label: "7:00 AM", disabled: isTimeInPast("07:00") },
        { value: "07:30", label: "7:30 AM", disabled: isTimeInPast("07:30") },
      ],
    },
    midday: {
      primary: [
        { value: "12:00", label: "12:00 PM", disabled: isTimeInPast("12:00") },
        { value: "12:30", label: "12:30 PM", disabled: isTimeInPast("12:30") },
        { value: "13:00", label: "1:00 PM", disabled: isTimeInPast("13:00") },
        { value: "13:30", label: "1:30 PM", disabled: isTimeInPast("13:30") },
        { value: "14:00", label: "2:00 PM", disabled: isTimeInPast("14:00") },
        { value: "14:30", label: "2:30 PM", disabled: isTimeInPast("14:30") },
        { value: "15:00", label: "3:00 PM", disabled: isTimeInPast("15:00") },
        { value: "15:30", label: "3:30 PM", disabled: isTimeInPast("15:30") },
        { value: "16:00", label: "4:00 PM", disabled: isTimeInPast("16:00") },
        { value: "16:30", label: "4:30 PM", disabled: isTimeInPast("16:30") },
        { value: "17:00", label: "5:00 PM", disabled: isTimeInPast("17:00") },
        { value: "17:30", label: "5:30 PM", disabled: isTimeInPast("17:30") },
      ],
      secondary: [],
    },
    evening: {
      primary: [
        { value: "18:00", label: "6:00 PM", disabled: isTimeInPast("18:00") },
        { value: "18:30", label: "6:30 PM", disabled: isTimeInPast("18:30") },
        { value: "19:00", label: "7:00 PM", disabled: isTimeInPast("19:00") },
        { value: "19:30", label: "7:30 PM", disabled: isTimeInPast("19:30") },
        { value: "20:00", label: "8:00 PM", disabled: isTimeInPast("20:00") },
        { value: "20:30", label: "8:30 PM", disabled: isTimeInPast("20:30") },
        { value: "21:00", label: "9:00 PM", disabled: isTimeInPast("21:00") },
        { value: "21:30", label: "9:30 PM", disabled: isTimeInPast("21:30") },
      ],
      secondary: [
        { value: "22:00", label: "10:00 PM", disabled: isTimeInPast("22:00") },
        { value: "22:30", label: "10:30 PM", disabled: isTimeInPast("22:30") },
        { value: "23:00", label: "11:00 PM", disabled: isTimeInPast("23:00") },
        { value: "23:30", label: "11:30 PM", disabled: isTimeInPast("23:30") },
      ],
    },
  };

  const tabs: { label: string; primary: TimeSlot[]; secondary: TimeSlot[] }[] = [
    { label: t("sessionForm.morningSlots"), primary: timeSlots.morning.primary, secondary: timeSlots.morning.secondary },
    { label: t("sessionForm.middaySlots"), primary: timeSlots.midday.primary, secondary: timeSlots.midday.secondary },
    { label: t("sessionForm.eveningSlots"), primary: timeSlots.evening.primary, secondary: timeSlots.evening.secondary },
  ];

  // Helper function to find the next available time slot
  const getNextAvailableTimeSlot = useCallback((): string | null => {
    const allSlots = [
      ...timeSlots.morning.primary, ...timeSlots.morning.secondary,
      ...timeSlots.midday.primary, ...timeSlots.midday.secondary,
      ...timeSlots.evening.primary, ...timeSlots.evening.secondary,
    ].sort((a, b) => a.value.localeCompare(b.value));

    // Find the first non-disabled slot
    const availableSlot = allSlots.find((slot) => !slot.disabled);
    return availableSlot ? availableSlot.value : null;
  }, [timeSlots.morning, timeSlots.midday, timeSlots.evening]);

  // Get all predefined time values
  const getAllPredefinedTimes = () => {
    return [
      ...timeSlots.morning.primary, ...timeSlots.morning.secondary,
      ...timeSlots.midday.primary, ...timeSlots.midday.secondary,
      ...timeSlots.evening.primary, ...timeSlots.evening.secondary,
    ].map((slot) => slot.value);
  };

  // Check if a custom time is selected (not one of the predefined options)
  const predefinedTimes = getAllPredefinedTimes();
  const isCustomTimeSelected =
    selectedTime && !predefinedTimes.includes(selectedTime);

  const handleTimeSelect = (time: string) => {
    if (disabled) return;

    // Hide custom input when predefined time is selected
    setShowCustomInput(false);
    setCustomTimeError("");

    // Reset manual tab change flag when user selects a time
    setIsManualTabChange(false);

    if (onTimeSelect) {
      onTimeSelect(time);
    }
  };

  const handleCustomInputToggle = useCallback(() => {
    if (disabled) return;

    if (showCustomInput) {
      // Hide custom input - revert to predefined selection if available
      setShowCustomInput(false);
      setCustomTimeError("");
      // If there was a predefined time selected before, keep it
      // Otherwise, clear the selection
      if (!predefinedTimes.includes(selectedTime || "")) {
        if (onTimeSelect) {
          onTimeSelect("");
        }
      }
    } else {
      // Show input - use existing custom time or set default
      setShowCustomInput(true);
      const timeToUse = customTime || "00:00"; // Use existing custom time or default
      if (!customTime) {
        setCustomTime(timeToUse);
      }
      if (onTimeSelect) {
        onTimeSelect(timeToUse);
      }
    }
  }, [
    disabled,
    showCustomInput,
    selectedTime,
    predefinedTimes,
    onTimeSelect,
    customTime,
  ]);

  const handleCustomTimeChange = useCallback(
    (time: string) => {
      setCustomTime(time);

      // Clear previous error when user starts selecting
      if (customTimeError) {
        setCustomTimeError("");
      }

      // Check if the entered time matches any predefined time
      if (time && isValidTimeFormat(time) && predefinedTimes.includes(time)) {
        // Auto-switch to predefined time slot but keep custom input visible
        const correctTab = getTabForTime(time);
        setActiveTab(correctTab);
        setIsManualTabChange(false); // Reset manual tab change flag for auto-switch
        // Keep custom input visible so user can continue to make adjustments
        if (onTimeSelect) {
          onTimeSelect(time);
        }
        return;
      }

      // Validate and show error if invalid format
      if (time && !isValidTimeFormat(time)) {
        setCustomTimeError(t("sessionForm.pleaseSelectValidTime"));
      } else {
        setCustomTimeError("");
      }

      // Only call onTimeSelect if custom input is active and time is not predefined
      if (showCustomInput && onTimeSelect) {
        onTimeSelect(time);
      }
    },
    [
      onTimeSelect,
      customTimeError,
      showCustomInput,
      predefinedTimes,
      getTabForTime,
    ]
  );

  // Update active tab when selectedTime changes (but not when custom input is active or user manually changed tab)
  useEffect(() => {
    if (
      selectedTime &&
      predefinedTimes.includes(selectedTime) &&
      !showCustomInput &&
      !isManualTabChange
    ) {
      const correctTab = getTabForTime(selectedTime);
      setActiveTab(correctTab);
    }
  }, [
    selectedTime,
    predefinedTimes,
    getTabForTime,
    showCustomInput,
    isManualTabChange,
  ]);

  // Sync custom time input with selected time when it's a custom value
  useEffect(() => {
    if (isCustomTimeSelected && !showCustomInput) {
      // Only auto-show custom input if it's not already showing
      setCustomTime(selectedTime || "");
      setShowCustomInput(true);
    }
    // Removed the auto-hiding logic to prevent conflicts with manual toggle
  }, [selectedTime, isCustomTimeSelected, showCustomInput]);

  // Smart default selection: auto-select the next available time slot when no time is selected
  useEffect(() => {
    if (!selectedTime && !isManualTabChange && selectedDate && onTimeSelect) {
      const nextAvailableTime = getNextAvailableTimeSlot();
      if (nextAvailableTime) {
        // Auto-select the next available time slot
        onTimeSelect(nextAvailableTime);
        // Update the active tab to show the selected time
        const correctTab = getTabForTime(nextAvailableTime);
        setActiveTab(correctTab);
      }
    }
  }, [
    selectedTime,
    selectedDate,
    isManualTabChange,
    onTimeSelect,
    getNextAvailableTimeSlot,
    getTabForTime,
  ]);

  // Update active tab when selectedDate changes to show the most appropriate tab
  useEffect(() => {
    if (selectedDate && !selectedTime && !isManualTabChange) {
      const bestTab = getBestDefaultTab();
      setActiveTab(bestTab);
    }
  }, [selectedDate, selectedTime, isManualTabChange, getBestDefaultTab]);

  return (
    <div className={`space-y-2 ${className}`} data-testid="time-selector">
      <p className="font-bold">{t("sessionForm.startTime")}</p>

      <div className={`start-time-tabs ${disabled ? "opacity-50" : ""}`}>
        <div className="tabs-header" role="tablist">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`tab-trigger group ${
                activeTab === index ? "active" : ""
              }`}
              role="tab"
              aria-selected={activeTab === index}
              aria-controls={`panel-${index}`}
              id={`tab-${index}`}
              tabIndex={activeTab === index ? 0 : -1}
              onClick={() => {
                if (!disabled) {
                  setActiveTab(index);
                  setIsManualTabChange(true);
                }
              }}
              type="button"
              disabled={disabled}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tabs-content mt-5">
          {tabs.map((tab, tabIndex) => {
            const renderSlot = (slot: TimeSlot, index: number, group: string) => {
              const inputId = `time-slot-${tabIndex}-${group}-${index}`;
              return (
                <label
                  key={`${tabIndex}-${group}-${index}`}
                  htmlFor={inputId}
                  className={`flex ${
                    disabled || slot.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!disabled && !slot.disabled) {
                      handleTimeSelect(slot.value);
                    }
                  }}
                >
                  <input
                    id={inputId}
                    type="radio"
                    name="available_time"
                    value={slot.value}
                    className="peer hidden"
                    disabled={slot.disabled || disabled}
                    checked={selectedTime === slot.value}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleTimeSelect(slot.value);
                    }}
                  />
                  <span className="checkbox-group-item py-0">
                    {slot.label}
                  </span>
                </label>
              );
            };

            return (
              <div
                key={tabIndex}
                id={`panel-${tabIndex}`}
                className={`tab-content ${
                  activeTab === tabIndex ? "animate-fadeIn" : "hidden"
                }`}
                role="tabpanel"
                aria-labelledby={`tab-${tabIndex}`}
                {...(activeTab !== tabIndex && { "aria-hidden": "true" })}
              >
                <div className="grid max-w-[476px] grid-cols-[repeat(4,minmax(0,1fr))] gap-4">
                  {tab.primary.map((slot, index) => renderSlot(slot, index, "primary"))}
                </div>
                {tab.secondary.length > 0 && (
                  <>
                    <div className="my-4 max-w-[476px] border-t border-grey-80 dark:border-grey-40" />
                    <div className="grid max-w-[476px] grid-cols-[repeat(4,minmax(0,1fr))] gap-4">
                      {tab.secondary.map((slot, index) => renderSlot(slot, index, "secondary"))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={`${disabled ? "opacity-50" : ""}`}>
        <button
          className={`btn mt-4 transition-all duration-200 ${
            showCustomInput ? "btn-primary" : "btn-secondary"
          }`}
          onClick={handleCustomInputToggle}
          type="button"
          disabled={disabled}
        >
          <span>{t("sessionForm.enterYourStartTime")}</span>
        </button>

        {/* Animated custom time input */}
        <div
          className={`transition-all duration-300 ease-in-out ${
            showCustomInput
              ? "max-h-96 opacity-100"
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
        >
          <div className="pt-4">
            <TimePicker
              label={t("sessionForm.customStartTime")}
              value={customTime}
              onChange={handleCustomTimeChange}
              error={customTimeError}
              useScrollPicker={true}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Error message for time selection */}
      {error && (
        <p className="field-error mt-1">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
              fill="currentColor"
            />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export default TimeSelector;
